import { Fragment, useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { ShowField } from "../../components/ViewApplication/ViewApplication";
import { OfflineTentativeDetailsSummary } from "../../components/OfflineTentativeDetails/OfflineTentativeDetails";
import { InwardOutwardSummary } from "../../components/InwardOutwardSummary/InwardOutwardSummary";
import { SchematicPlanFileRow } from "../../components/SchematicPlan/SchematicPlanFileRow";
import { makeStepIndexOf, sequenceReviewFields } from "../../components/FieldSequence/fieldSequenceOrder";
import { isListedOnDetailScreen } from "../../utils/detailScreenFields";
/**
 * Not imported while detail screens leave the Admin's empty spaces out
 * (they only apply to the applicant form); this was the blank row drawn
 * for each one:
 * import { ReviewFieldSpacer } from "../../components/FieldSequence/ReviewFieldSpacer";
 */
import { useReviewFieldSequence } from "../../hooks/field-sequence.hooks";
import { NavbarApplicationInfo } from "../../components/CommonComponents/Navbar/NavbarApplicationInfo";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel1 } from "../../hoc/isLevel1";
import {
  useAllFields,
  useApplicationFormSteps,
  useApplicationLogs,
} from "../../hooks/applications.hooks";
import { useApplicationForLevel1 } from "../../hooks/level-1-application-approval.hooks";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import html2pdf from 'html2pdf.js';
import { APPLICATION_PDF_OPTIONS } from '../../utils/applicationPdf';
import { useToggle } from "../../hooks/common";
import { RenderAllLogs } from "../../pages/LevelTwo/RenderLogs";
import { PLOT_AND_LAYOUT_FIELD_UIDS } from "../../config/phaseConstants";
import { ReviewerEditButton } from "../../components/ReviewerEditApplication/ReviewerEditButton";

export interface ViewApplicationForLevel1Props {
  onNext?: () => void;
}

export const ViewApplicationForLevel1: React.FC<ViewApplicationForLevel1Props> = isLevel1(
  ({ onNext }: ViewApplicationForLevel1Props = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;
  const [logsDialog, toggleLogsDialog] = useToggle();
  const allLogsQuery = useApplicationLogs({ id: +id });

  const contentRef = useRef(null);
  // Ref to the content to be captured for the PDF
  const handleDownloadPDF = () => {
    // Get the element to generate the PDF from
    const content = contentRef.current;
    /**
     * Shared options that keep each field row whole on one page
     * (see utils/applicationPdf.ts).
     *
     * BEFORE - no page-break rules, so a row at the bottom of a page was
     * cut in half:
     * const options = {
     *   margin: 1,
     *   filename: 'page.pdf',
     *   image: { type: 'jpeg', quality: 0.98 },
     *   html2canvas: { scale: 2 },
     *   jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
     * };
     */
    const options = APPLICATION_PDF_OPTIONS;
    // const updatedData = `<div><p>Application Id</p>${content}</div>`
    // Generate and download the PDF
    html2pdf().from(content).set(options).save();
  };

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationForLevel1({ id: String(id) });

  const {
    data: result,
    isError: isError2,
    isFetching: isFetching2,
  // } = useApplicationFormSteps(Number(application?.name_of_service));
  } = useApplicationFormSteps(application?.name_of_service as any);
  
  const {
    isFetching: isFetching3,
    isError: isError3,
    data: allFields,
  } = useAllFields({
    serviceName: String(application?.name_of_service || ""),
  });

  /**
   * The Admin's Field Sequence for the application details below, or
   * null while its review screens switch is off (default order).
   */
  const { data: reviewFieldOrder } = useReviewFieldSequence();

  const steps = result?.data || [];
  const isFetching = isFetching1 || isFetching2;

  const selectedFields = useMemo(
    () =>
      (steps || [])
        .filter((e) => !["Documents", "NOC"].includes(e.type))
        .flatMap((e) => e.fields || [])
        .reduce<Map<string, true>>((acc, e) => acc.set(e.uid, true), new Map()),
    [steps]
  );

  const uniqueFields = useMemo(() => {
    const seen = new Set();
    const hasConstruction =
      Boolean(application?.is_there_a_construction) ||
      String(application?.is_there_a_construction) === "1" ||
      String(application?.is_there_a_construction).toLowerCase() === "true" ||
      String(application?.is_there_a_construction).toLowerCase() === "yes";

    const filtered = (allFields || []).filter((e) => {
      if (!e.uid || e.uid === "land_possession_certificate_url") return false;
      if ((e.uid === "construction_area" || e.uid === "built_up_area") && (!hasConstruction || Number(application?.[e.uid] || 0) <= 0)) {
        return false;
      }
      if (seen.has(e.uid)) return false;
      seen.add(e.uid);
      return true;
    });
    
    if (!seen.has("address")) {
      filtered.push({
        id: -1,
        uid: "address",
        name: "Address",
        label: "Address",
        type: "text",
      } as any);
    }
    return filtered;
  }, [allFields, application]);

  const isError =
    isError1 || isError2 || (!isFetching && (!application || !steps?.length));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <NavbarApplicationInfo
        applicationId={application?.applicationId}
        status={application?.status}
        applicationType={application?.rl_application_type}
        currentPhase={application?.current_phase}
      />
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  if (isNaN(+String(id))) {
    return <Navigate to={"/user-applications"} replace />;
  }

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  const normalizedApplication = {
    ...application,
    // map camelCase → snake_case
    type_of_application_submitter:
      application?.type_of_application_submitter ??
      application?.typeOfApplicationSubmitter,

    submitted_by:
      application?.submitted_by ??
      application?.submittedBy,
  };

  const PrintData = () => {
    return <div style={{ display: 'none' }}>
      <div ref={contentRef}>
        <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
          Application
        </h4>
        {/**
          * BEFORE: the labels had different widths and a long status ran on
          * one line. Now both labels are w-28 so the colons line up, and the status wraps at 240px.
          <div className="flex flex-col ml-auto mr-8 justify-items-end items-end">
            <p className="text-grey-600">
              <span className="w-28 font-semibold inline-block">
                Application ID
              </span>
              <span className="text-grey-600 font-semibold mx-2">:</span>
              <span>{application?.applicationId || "Nil"}</span>
            </p>
            <p className="text-grey-600 flex items-center">
              <span className="text-grey-600 font-semibold inline-block">
                Status
              </span>
              <span className="text-grey-600 font-semibold mx-2">:</span>
              <span className="text-primary">{application?.status || "Nil"}</span>
            </p>
          </div>
          */}
        <div className="flex flex-col ml-auto mr-8 w-fit">
          <p className="text-grey-600 flex items-start">
            <span className="w-28 shrink-0 font-semibold inline-block">
              Application ID
            </span>
            <span className="text-grey-600 font-semibold mx-2">:</span>
            <span>{application?.applicationId || "Nil"}</span>
          </p>
          <p className="text-grey-600 flex items-start">
            <span className="w-28 shrink-0 text-grey-600 font-semibold inline-block">
              Status
            </span>
            <span className="text-grey-600 font-semibold mx-2">:</span>
            <span className="text-primary max-w-[240px] break-words">
              {application?.status || "Nil"}
            </span>
          </p>
        </div>
        <ApplicationList />
      </div>
    </div>
  }


  const ApplicationList = () => {
    let i = 0;
    return <div>
      {/**
         * The rows follow the Admin's Field Sequence when its review
         * screens switch is on (the Admin\'s empty spaces are left out;
         * they only apply to the applicant form); otherwise sequenceReviewFields hands the rows back in
         * their default order.
         *
         * BEFORE - always the default order:
         * {uniqueFields
         *   .filter((e) => selectedFields.has(e.uid) ||
         *     !!application?.[e.uid] ||
         *     e.uid === "typeOfApplicationSubmitterBy" ||
         *     e.uid === "submittedBy" ||
         *     (PLOT_AND_LAYOUT_FIELD_UIDS as readonly string[]).includes(e.uid))
         *   .map((e) => (
         *     <ShowField
         *       field={e}
         *       index={++i}
         *       key={`field-${e.id}`}
         *       data={normalizedApplication || {}}
         *     />
         *   ))}
         */}
      {sequenceReviewFields(
        uniqueFields
          .filter((e) => selectedFields.has(e.uid) ||
            !!application?.[e.uid] ||
            e.uid === "typeOfApplicationSubmitterBy" ||
            e.uid === "submittedBy" ||
            (PLOT_AND_LAYOUT_FIELD_UIDS as readonly string[]).includes(e.uid))
          /** Main Khasara No. and empty Inactive fields are left out (see detailScreenFields.ts). */
          .filter((field) => isListedOnDetailScreen(field, application)),
        reviewFieldOrder,
        /** Fields missing from the order go to the end of their own form step. */
        makeStepIndexOf(steps || [])
      ).map((field) => (
        <ShowField
          field={field}
          longApplicationType
          index={++i}
          key={`field-${field.id}`}
          data={normalizedApplication || {}}
        />
      ))}
      {/* The Offline Tentative reference details are not form-config fields,
          so the loop above never lists them. They follow it here, numbered on
          from the last row, for that application type only. */}
      <OfflineTentativeDetailsSummary
        application={normalizedApplication}
        startIndex={i}
      />
      <InwardOutwardSummary
        application={application}
        startIndex={i}
      />
      {/* The uploaded Schematic Plan file, when the certificate uses one instead of the drawing. */}
      <SchematicPlanFileRow applicationId={application?.id} className="mt-6" />
    </div>
  }

  if (logsDialog) {
    return (
      <div className="px-4 pb-4 md:p-4">
        <div className="bg-white p-3 md:p-8">
          <h4 className="text-center text-grey-600 font-semibold mb-4 text-2xl">
            Audit History
          </h4>
          <div className="flex justify-start mb-4 gap-6">
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500"
              onClick={toggleLogsDialog}
            >
              <i className="fa fa-arrow-left"></i>
              Back
            </button>
          </div>
          <RenderAllLogs
            onClose={toggleLogsDialog}
            logs={allLogsQuery.data ?? []}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4 md:p-4">
      <div className="bg-white p-8">
        <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
          Application
        </h4>
        <div className="flex justify-end mb-4 gap-6">
          {!!allLogsQuery.data?.length && (
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500"
              onClick={toggleLogsDialog}
            >
              Audit History
            </button>
          )}

          <button
            className="text-blue-600 underline"
            onClick={() =>
              window.open("/blocked-plots-viewer", "_blank")
            }
          >
            List of Blocked Plots
          </button>

        </div>
        <ApplicationList />
        <PrintData />
        <div className="d-flex gap-5 justify-center items-center mt-4">

          <button
            className="bg-black ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300"
            onClick={() =>
              navigate(
                location.pathname.includes("/accountant")
                  ? "/accountant-receipts"
                  : "/level-1-applications"
              )
            }
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Download
          </button>
          {/* Lets the Level 1 reviewer correct the application's form fields.
              The button decides for itself whether to show: it is hidden for
              the Accountant, who opens this same screen, and disabled once
              the application has left Level 1. */}
          <ReviewerEditButton application={application} />
          <button
            type="button"
            onClick={() => {
              if (onNext) {
                onNext();
              } else if (location.pathname.includes("/accountant")) {
                // If on accountant page without onNext callback, fallback to accountant receipts
                navigate("/accountant-receipts");
              } else {
                navigate("/level-1-verify-application", { state: location.state });
              }
            }}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
});
